import { useQuery } from '@tanstack/react-query'
import { Heart, LogOut, Phone, User } from 'lucide-react'
import { formatMoney, formatNumber } from '@najah/core'
import { Badge, Button, Card, CardBody, EmptyState } from '@najah/ui'
import { api, type StoreProduct } from '../lib/api'
import { useAuth } from '../lib/auth'
import { AuthScreen } from './AuthScreen'
import { PushSettings } from '../components/PushSettings'

export function AccountScreen({ onOpenProduct }: { onOpenProduct: (id: string) => void }) {
  const { customer, logout } = useAuth()

  const { data: favorites } = useQuery({
    queryKey: ['favorites'],
    queryFn: () => api<StoreProduct[]>('/favorites'),
    enabled: !!customer,
  })

  if (!customer) return <AuthScreen />

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardBody className="flex items-center gap-4">
          <span className="grid size-14 place-items-center rounded-full bg-primary-subtle text-primary-subtle-fg">
            <User className="size-7" />
          </span>
          <div className="min-w-0">
            <p className="text-lg font-bold text-fg">{customer.fullName}</p>
            <p className="flex items-center gap-1.5 text-sm text-fg-muted" dir="ltr">
              <Phone className="size-3.5" />
              {customer.phone}
            </p>
          </div>
        </CardBody>
      </Card>

      <PushSettings />

      {customer.creditBalance > 0 && (
        <Card>
          <CardBody>
            <p className="text-sm text-fg-muted">دين مستحق عليك</p>
            <p className="mt-0.5 text-xl font-bold text-warning" dir="ltr">
              {formatMoney(customer.creditBalance)}
            </p>
          </CardBody>
        </Card>
      )}

      <div>
        <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-fg">
          <Heart className="size-4" />
          المفضّلة
          {favorites && favorites.length > 0 && <Badge tone="neutral">{formatNumber(favorites.length)}</Badge>}
        </h2>

        {favorites && favorites.length > 0 ? (
          <ul className="space-y-2">
            {favorites.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => onOpenProduct(p.id)}
                  className="flex w-full items-center justify-between gap-3 rounded-xl border border-border bg-surface p-3 text-start"
                >
                  <span className="min-w-0 flex-1 truncate text-sm text-fg">{p.nameAr}</span>
                  <span className="shrink-0 font-semibold text-primary" dir="ltr">
                    {formatMoney(p.retailPrice)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="rounded-xl border border-dashed border-border">
            <EmptyState
              icon={<Heart className="size-8" />}
              title="لا توجد مفضّلات"
              description="اضغط القلب على أي منتج لحفظه هنا"
            />
          </div>
        )}
      </div>

      <Button
        variant="secondary"
        fullWidth
        onClick={() => void logout()}
        startIcon={<LogOut className="size-4 icon-flip" />}
        className="text-danger"
      >
        تسجيل الخروج
      </Button>
    </div>
  )
}
